import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthProvider';
import { getCSRFToken, fetchAndGetCSRFToken } from './utils/csrf';

function MFAManagement() {
  const { user } = useContext(AuthContext);
  const isAdmin = Boolean(
    user &&
    (
      user.is_staff ||
      user.is_superuser ||
      user.role_name === 'Admin' ||
      user.role_name === 'Super Admin' ||
      user.role_name === 'Associate admin' ||
      user.role_name === 'Associate Admin'
    )
  );
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState('email'); // email, name, or role
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [toast, setToast] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [resetting, setResetting] = useState(false);

  const ITEMS_PER_PAGE = 12;

  const fetchUsers = async (page = 1, query = '') => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: ITEMS_PER_PAGE,
      };

      if (query.trim() && searchField !== 'role') {
        params.search = query.trim();
      }

      const response = await axios.get('/api/v1/users/', {
        params,
        withCredentials: true,
      });

      const userList = response.data.results || response.data || [];
      setUsers(userList);
      setTotalUsers(response.data.count ?? userList.length);
      setCurrentPage(page);
    } catch (err) {
      showToast('Failed to load users.', 'error');
      console.error('[MFAManagement] Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchUsers(1, searchQuery);
    }, 600);

    return () => clearTimeout(handler);
  }, [searchQuery, searchField]);

  // Handle search
  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleSearchFieldChange = (field) => {
    setSearchField(field);
    setSearchQuery('');
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalUsers / ITEMS_PER_PAGE);
  const startIdx = totalUsers === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endIdx = Math.min(currentPage * ITEMS_PER_PAGE, totalUsers);
  const paginatedUsers = users;
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleResetMFA = async (targetUser) => {
    if (!confirming) {
      setConfirming(targetUser);
      return;
    }

    setResetting(true);
    try {
      await fetchAndGetCSRFToken();
      const csrfToken = getCSRFToken();
      const response = await axios.post(
        `/api/admin/users/${targetUser.id}/reset-mfa`,
        {},
        {
          headers: { 'X-CSRFToken': csrfToken },
          withCredentials: true,
        }
      );

      showToast(
        `MFA successfully reset. User ${targetUser.email} will be required to configure MFA again during next login.`,
        'success'
      );

      // Refresh the current page from the server
      await fetchUsers(currentPage, searchQuery);
      setConfirming(null);
    } catch (err) {
      const errorMsg =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to reset MFA.';
      showToast(errorMsg, 'error');
      console.error('[MFAManagement] Reset error:', err);
    } finally {
      setResetting(false);
    }
  };

  const cancelReset = () => {
    setConfirming(null);
  };

  // Check authorization (only after initial load)
  if (!loading && (!user || !isAdmin)) {
    return (
      <div className="p-8 text-center text-red-600">
        Access Denied. Only admins can access this page.
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen relative">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">MFA Management</h1>

        {/* Toast Notification */}
        {toast && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              toast.type === 'success'
                ? 'bg-green-100 text-green-700 border border-green-300'
                : toast.type === 'error'
                ? 'bg-red-100 text-red-700 border border-red-300'
                : 'bg-blue-100 text-blue-700 border border-blue-300'
            }`}
          >
            {toast.message}
          </div>
        )}

        {/* Search Section */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <div className="flex gap-4 mb-4">
            {['email', 'name', 'role'].map((field) => (
              <button
                key={field}
                onClick={() => handleSearchFieldChange(field)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  searchField === field
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Search by {field.charAt(0).toUpperCase() + field.slice(1)}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder={`Search by ${searchField}...`}
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
          />
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {paginatedUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-600">
              No users found.
            </div>
          ) : (
            <>
              <table className="w-full border-collapse">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Role
                    </th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">
                      MFA Enabled
                    </th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">
                      MFA Setup
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="px-6 py-3 text-sm text-gray-900">
                        {u.full_name || 'N/A'}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-900">
                        {u.email}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-900">
                        {u.role_name ||
                          (u.role ? (typeof u.role === 'string' ? u.role : u.role.name) : 'N/A')}
                      </td>
                      <td className="px-6 py-3 text-center text-sm">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            u.mfa_enabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {u.mfa_enabled ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-center text-sm">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            u.mfa_setup_completed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {u.mfa_setup_completed ? 'Complete' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600">
                        {u.last_login
                          ? new Date(u.last_login).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-6 py-3 text-center">
                        <button
                          onClick={() => handleResetMFA(u)}
                          className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition"
                        >
                          Reset MFA
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {startIdx} to {endIdx} of {totalUsers} users
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchUsers(Math.max(1, currentPage - 1), searchQuery)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 bg-gray-300 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-400 transition"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .slice(
                        Math.max(0, currentPage - 2),
                        Math.min(totalPages, currentPage + 1)
                      )
                      .map((pageNum) => (
                        <button
                          key={pageNum}
                          onClick={() => fetchUsers(pageNum, searchQuery)}
                          className={`px-3 py-1 rounded-lg transition ${
                            currentPage === pageNum
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {pageNum}
                        </button>
                      ))}
                  </div>
                  <button
                    onClick={() => fetchUsers(Math.min(totalPages, currentPage + 1), searchQuery)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 bg-gray-300 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-400 transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirming && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 p-8">
            <h3 className="text-xl font-bold mb-4 text-gray-900">
              Confirm MFA Reset
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to reset MFA for <strong>{confirming.email}</strong>?
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 mb-6 space-y-2">
              <li>All active sessions will be invalidated</li>
              <li>Current authenticator configuration will be removed</li>
              <li>MFA setup will be required again on next login</li>
            </ul>
            <div className="flex gap-4">
              <button
                onClick={cancelReset}
                disabled={resetting}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-400 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleResetMFA(confirming)}
                disabled={resetting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition disabled:opacity-50"
              >
                {resetting ? 'Resetting...' : 'Confirm Reset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50">
          <div className="text-center text-gray-700">Loading MFA Management...</div>
        </div>
      )}
    </div>
  );
}

export default MFAManagement;
